import {ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {BaseOption} from "./types";
import {Store} from "@ngrx/store";
import * as AdvancedSearchActions from "../../state/advancedSearch/advanced-search.actions";
import {
  selectAdvancedSearchFailure,
  selectAdvancedSearchSuccess,
  selectCompanyIndustries,
  selectCompanyIndustriesError,
  selectHasSavedFilters,
  selectSavedFilters,
  selectCountriesList,
  selectRegionsForCountry,
  selectCitiesForRegion,
  selectCountriesLoading,
  selectRegionLoading,
  selectCityLoading, selectTotalRecords
} from "../../state/advancedSearch/advanced-search.selectors";
import {PopupService} from "../../services/popup.service";
import {combineLatest, filter, skip, Subject, Subscription, take, takeUntil} from "rxjs";
import {HeaderService} from "../../services/header.service";
import {Router} from "@angular/router";

// Custom validator for range inputs
function rangeValidator(control: AbstractControl): { [key: string]: any } | null {
  const value = control.value;

  if (!value) {
    return null;
  }

  if (Array.isArray(value)) {
    const [min, max] = value;

    if ((min === null || min === '') || (max === null || max === '')) {
      return { 'incompleteRange': true };
    }
  }

  return null;
}

interface FilterOption {
  id: number;
  name: string;
  attribute: string;
  type: 'select' | 'input' | 'range' | 'tags' | 'location' | 'combobox-multiselect' | 'proximity';
  relation: 'equals' | 'in' | 'between' | 'match_expression' | 'within' | string;
  applied?: string;
  hidden?: boolean;
  prefix?: string;
  suffix?: string;
  max?: number;
  options?: {
    id: number;
    name: string;
    value?: string;
  }[];
}

function proximityValidator(control: AbstractControl): { [key: string]: any } | null {
  const value = control.value;
  if (!value || typeof value !== 'object') return null;
  const addressOk = typeof value.address === 'string' && value.address.trim().length >= 2;
  const radius = typeof value.radius === 'number' ? value.radius : Number(value.radius);
  const radiusOk = Number.isFinite(radius) && radius > 0;
  const unitOk = value.unit === 'km' || value.unit === 'mi';
  const strictnessOk = value.strictness === 1 || value.strictness === 2 || value.strictness === 3;
  return (addressOk && radiusOk && unitOk && strictnessOk) ? null : { incompleteProximity: true };
}

@Component({
  selector: 'app-advanced-search',
  templateUrl: './advanced-search.component.html',
  styleUrl: './advanced-search.component.scss',
})
export class AdvancedSearchComponent implements OnInit, OnDestroy {

  form: FormGroup;
  private destroy$ = new Subject<void>();

  // Separate storage for person filters
  private selectedPersonTitles: string[] = [];
  private selectedPersonSeniorities: string[] = [];

  filterTypes: FilterOption[] = [
    {
      id: 10,
      name: 'Company Location',
      attribute: 'company_location',
      type: 'location',
      relation: 'equals'
    },
    {
      id: 1,
      name: 'Company Name',
      attribute: 'company_name',
      type: 'input',
      relation: 'equals'
    },
    {
      id: 2,
      name: 'Company Website',
      attribute: 'company_website',
      type: 'input',
      relation: 'equals'
    },
    {
      id: 3,
      name: 'Company Postcode',
      attribute: 'company_postcode',
      type: 'input',
      relation: 'equals'
    },
    {
      id: 14,
      name: 'Company Proximity',
      attribute: 'company_location',
      type: 'proximity',
      relation: 'within'
    },
    {
      id: 13,
      name: 'Company Industry',
      attribute: 'company_industry',
      type: 'combobox-multiselect',
      relation: 'in',
      options: []
    },
    {
      id: 4,
      name: 'Company Business Category',
      attribute: 'company_category',
      type: 'combobox-multiselect',
      relation: 'in',
      options: []
    },
    {
      id: 5,
      name: 'Company Employee Count',
      attribute: 'company_employee_count',
      type: 'range',
      relation: 'between'
    },
    {
      id: 6,
      name: 'Company Estimated Revenue',
      attribute: 'company_estimated_revenue',
      type: 'range',
      relation: 'between',
      prefix: '$',
      suffix: 'USD',
      max: 999_999_999_999
    },
    {
      id: 7,
      name: 'Company Year Founded',
      attribute: 'company_year_founded',
      type: 'input',
      relation: 'equals'
    },
    {
      id: 8,
      name: 'Company Keywords',
      attribute: 'company_keywords',
      type: 'tags',
      relation: 'match_expression'
    },
    {
      // Veridion `company_products` (https://docs.veridion.com/reference/company-products):
      // plural attribute + match_expression; terms are OR-ed. Sent as a root-level `and` condition.
      id: 9,
      name: 'Company Product',
      attribute: 'company_products',
      type: 'tags',
      relation: 'match_expression'
    },
    {
      id: 11,
      name: 'Person Titles',
      attribute: 'person_titles',
      applied: 'person',
      type: 'tags',
      relation: 'match_expression'
    },
    {
      id: 12,
      name: 'Person Seniorities',
      attribute: 'person_seniorities',
      applied: 'person',
      type: 'combobox-multiselect',
      relation: 'in',
      options: [
        { id: 1, name: 'owner' },
        { id: 2, name: 'founder' },
        { id: 3, name: 'c_suite' },
        { id: 4, name: 'partner' },
        { id: 5, name: 'vp' },
        { id: 6, name: 'head' },
        { id: 7, name: 'director' },
        { id: 8, name: 'manager' },
        { id: 9, name: 'senior' },
        { id: 10, name: 'entry' },
        { id: 11, name: 'intern' }
      ]
    }
  ];

  countries: BaseOption[] = [];
  regions: {[countryId: number]: BaseOption[]} = {};
  cities: {[regionId: number]: BaseOption[]} = {};

  private advancedSearchSuccessSubscription: Subscription;
  private advancedSearchFailureSubscription: Subscription;
  private industriesSubscription: Subscription | null = null;

  // Memoized Business Category options narrowed to the selected Company Industries.
  // Must keep a stable array reference between change-detection cycles, otherwise the
  // combobox's ngOnChanges re-initializes on every cycle and wipes its search term.
  private categoryOptionsCache: { key: string; source: any[]; options: any[] } | null = null;

  // Store subscriptions
  private countriesSubscription: Subscription;
  private regionsSubscriptions: { [key: string]: Subscription } = {};
  private citiesSubscriptions: { [key: string]: Subscription } = {};

  constructor(
    private fb: FormBuilder,
    private store: Store,
    private popupService: PopupService,
    private cdr: ChangeDetectorRef,
    private headerService: HeaderService,
    private router: Router
  ) {
    this.initForm();
  }

  private initForm() {
    this.form = this.fb.group({
      filters: this.fb.array([])
    });

    // Add default location filter
    this.addDefaultLocationFilter();
  }

  private addDefaultLocationFilter() {
    const filterGroup = this.fb.group({
      filterType: [{value: 10, disabled: true}, Validators.required],
      value: this.fb.group({
        country: [null, Validators.required],
        region: [{value: null, disabled: true}],
        city: [{value: null, disabled: true}]
      }),
      isDefault: [true]
    });

    const filtersArray = this.form.get('filters') as FormArray;
    filtersArray.push(filterGroup);
  }

  ngOnInit(): void {
    this.headerService.setHeaderState('Advanced Prospecting', 'Pick a filter, choose a value. Add more if you like, then save as query of search.', true);

    // Load saved filters if available
    this.store.dispatch(AdvancedSearchActions.loadSavedFilters());

    // Load countries first
    this.loadCountries();

    // Load company industries
    this.initializeCompanyIndustries();

    // Check if we have saved filters after countries are loaded
    this.store.select(selectCountriesList)
      .pipe(
        filter(countries => countries && countries.length > 0),
        take(1)
      )
      .subscribe(() => {
        // Now check for saved filters
        this.store.select(selectHasSavedFilters)
          .pipe(take(1))
          .subscribe(hasSaved => {
            if (hasSaved) {
              this.store.select(selectSavedFilters)
                .pipe(take(1))
                .subscribe(savedFilters => {
                  if (savedFilters && savedFilters.filters && savedFilters.filters.length > 0) {
                    this.restoreFiltersFromSaved(savedFilters.filters);
                  }
                });
            }
          });
      });
  }

  private loadCountries(): void {
    // Check if countries are already loaded
    this.countriesSubscription = this.store.select(selectCountriesList)
      .pipe(take(1))
      .subscribe((countries: BaseOption[]) => {
        if (countries.length === 0) {
          this.store.dispatch(AdvancedSearchActions.loadCountries());
        } else {
          this.countries = countries;
          this.cdr.detectChanges();
        }
      });

    // Subscribe to countries updates
    this.store.select(selectCountriesList)
      .pipe(takeUntil(this.destroy$))
      .subscribe((countries: BaseOption[]) => {
        this.countries = countries;
        this.cdr.detectChanges();
      });
  }

  private initializeCompanyIndustries(): void {
    this.store.dispatch(AdvancedSearchActions.searchCompanyIndustries({ searchTerm: '' }));

    // If the industries provider (Veridion) is unavailable, the load fails without
    // logging the user out — surface a non-blocking notice instead.
    this.store.select(selectCompanyIndustriesError)
      .pipe(
        filter(error => !!error),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.popupService.notify(
          'This functionality is currently not available. Please try again later.',
          'Advanced Search',
          'warning'
        );
      });

    this.industriesSubscription = this.store.select(selectCompanyIndustries)
      .pipe(
        filter(industries => industries && industries.length > 0),
        take(1)
      )
      .subscribe(industries => {
        const flattenedCategories = [];

        industries.forEach(industry => {
          if (!industry.children || industry.children.length === 0) {
            return;
          }

          industry.children.forEach(child => {
            flattenedCategories.push({
              id: `${industry.name}:${child.name}`,
              name: child.name,
              parentName: industry.name,
              originalType: child.type
            });
          });
        });

        const industryFilterIndex = this.filterTypes.findIndex(filter => filter.id === 4);
        if (industryFilterIndex !== -1) {
          this.filterTypes[industryFilterIndex].options = flattenedCategories;
        }

        const rootIndustries = industries
          .filter(industry => industry?.name)
          .map(industry => ({ id: industry.name, name: industry.name }));
        const realIndustryFilterIndex = this.filterTypes.findIndex(filter => filter.id === 13);
        if (realIndustryFilterIndex !== -1) {
          this.filterTypes[realIndustryFilterIndex].options = rootIndustries;
        }

        this.cdr.detectChanges();
      });
  }

  get filters() {
    return this.form.get('filters') as FormArray;
  }

  addFilter() {
    const filterGroup = this.fb.group({
      filterType: [null, Validators.required],
      value: [null, Validators.required]
    });

    this.filters.push(filterGroup);
  }

  removeFilter(index: number) {
    const filterGroup = this.filters.at(index) as FormGroup;

    // Only the default (first) location filter is mandatory and protected.
    // Additional location filters can be removed like any other filter.
    if (filterGroup.get('isDefault')?.value) {
      this.popupService.notify('The default location filter cannot be removed', 'Info', 'info');
      return;
    }

    this.filters.removeAt(index);
  }

  onOptionSelected(index: number, option: any) {
    const filterGroup = this.filters.at(index) as FormGroup;
    if (filterGroup && option) {
      const filterTypeValue = filterGroup.get('filterType')?.value;
      const filterType = this.filterTypes.find(f => f.id === filterTypeValue);

      if (filterType && filterType.type === 'select') {
        filterGroup.get('value')?.setValue([option.name]);
      } else {
        filterGroup.get('value')?.setValue(option.name);
      }
    }
  }

  onMultiSelectChange(index: number, selectedOptions: any[]) {
    const filterGroup = this.filters.at(index) as FormGroup;
    if (filterGroup && selectedOptions) {
      const selectedNames = selectedOptions.map(option => option.name);
      const filterTypeId = filterGroup.get('filterType')?.value;

      if (filterTypeId === 11) {
        this.selectedPersonTitles = selectedNames;
      } else if (filterTypeId === 12) {
        this.selectedPersonSeniorities = selectedNames;
      }

      filterGroup.get('value')?.setValue(selectedNames);

      // Company Industry drives which Business Categories are valid.
      if (filterTypeId === 13) {
        this.syncCategoriesWithIndustries();
      }
    }
  }

  /**
   * Industries currently selected across all "Company Industry" filter rows.
   */
  private getSelectedIndustries(): string[] {
    const names = new Set<string>();
    this.filters.controls.forEach(control => {
      if (control.get('filterType')?.value !== 13) {
        return;
      }
      const value = control.get('value')?.value;
      if (Array.isArray(value)) {
        value.forEach(name => name && names.add(name));
      }
    });
    return Array.from(names);
  }

  /**
   * Business Category options, restricted to the children of the selected
   * Company Industries (industry -> category mapping from the taxonomy).
   * With no industry selected, every category is available.
   */
  private getCategoryOptionsForSelectedIndustries(): any[] {
    const allCategories: any[] = this.filterTypes.find(f => f.id === 4)?.options || [];
    const selectedIndustries = this.getSelectedIndustries();

    if (selectedIndustries.length === 0) {
      return allCategories;
    }

    const key = [...selectedIndustries].sort().join('\u0000');
    if (this.categoryOptionsCache
      && this.categoryOptionsCache.key === key
      && this.categoryOptionsCache.source === allCategories) {
      return this.categoryOptionsCache.options;
    }

    const industrySet = new Set(selectedIndustries);
    const options = allCategories.filter(option => industrySet.has(option.parentName));
    this.categoryOptionsCache = { key, source: allCategories, options };
    return options;
  }

  /**
   * Drops selected Business Categories that no longer belong to any selected
   * Company Industry, so the two filters can never contradict each other.
   */
  private syncCategoriesWithIndustries(): void {
    if (this.getSelectedIndustries().length === 0) {
      return;
    }

    const allowed = new Set(this.getCategoryOptionsForSelectedIndustries().map(option => option.name));
    let removedCount = 0;

    this.filters.controls.forEach(control => {
      if (control.get('filterType')?.value !== 4) {
        return;
      }
      const valueControl = control.get('value');
      const current = valueControl?.value;
      if (!Array.isArray(current) || current.length === 0) {
        return;
      }
      const kept = current.filter(name => allowed.has(name));
      if (kept.length !== current.length) {
        removedCount += current.length - kept.length;
        valueControl.setValue(kept);
        valueControl.markAsTouched();
      }
    });

    if (removedCount > 0) {
      this.popupService.notify(
        `${removedCount} business ${removedCount === 1 ? 'category was' : 'categories were'} removed because ${removedCount === 1 ? 'it doesn\'t' : 'they don\'t'} belong to the selected industries.`,
        'Advanced Search',
        'info'
      );
    }
  }

  getValueControl(filter: AbstractControl): FormControl {
    return filter.get('value') as FormControl;
  }

  onFilterTypeSelected(index: number, option: any) {
    const filterGroup = this.filters.at(index) as FormGroup;
    if (filterGroup) {
      if (option.id === 10) {
        const locationValueGroup = this.fb.group({
          country: [null, Validators.required],
          region: [{value: null, disabled: true}],
          city: [{value: null, disabled: true}]
        });

        filterGroup.setControl('value', locationValueGroup);
        filterGroup.patchValue({
          filterType: option.id
        });
      } else if (option.id === 4) {
        // setControl (not patchValue): switching away from a location type leaves
        // 'value' as a FormGroup, and patchValue can't replace a group with an
        // array — the stale group would then be bound to a non-location input.
        filterGroup.setControl('value', this.fb.control([], Validators.required));
        filterGroup.patchValue({
          filterType: option.id
        });
      } else if (option.type === 'range') {
        const valueControl = this.fb.control([null, null], [Validators.required, rangeValidator]);
        filterGroup.setControl('value', valueControl);
        filterGroup.patchValue({
          filterType: option.id
        });
      } else if (option.type === 'proximity') {
        const valueControl = this.fb.control(
          { address: '', radius: null, unit: 'km', strictness: 1 },
          [Validators.required, proximityValidator]
        );
        filterGroup.setControl('value', valueControl);
        filterGroup.patchValue({
          filterType: option.id
        });
      } else {
        // setControl (not patchValue): replaces any prior control — crucially a
        // location FormGroup — so input/select/tags bind to a real FormControl.
        filterGroup.setControl('value', this.fb.control(null, Validators.required));
        filterGroup.patchValue({
          filterType: option.id
        });
      }
    }
  }

  getFilterType(id: number): 'select' | 'input' | 'range' | 'tags' | 'location' | 'combobox-multiselect' | 'proximity' | null {
    const filter = this.filterTypes.find(f => f.id === id);
    return filter?.type || null;
  }

  getFilterOptions(id: number): BaseOption[] {
    if (!id || id === 0) {
      return this.filterTypes
        .filter(filter => filter.id !== 10 && !filter.hidden)
        .map(filter => ({
          id: filter.id,
          name: filter.name
        }));
    } else if (id === 4) {
      // Business Category depends on the selected Company Industries.
      return this.getCategoryOptionsForSelectedIndustries();
    } else {
      const filter = this.filterTypes.find(f => f.id === id);
      return filter?.options || [];
    }
  }

  getFilterName(id: number): string {
    const filter = this.filterTypes.find(f => f.id === id);
    return filter?.name || '';
  }

  getFilterPrefix(id: number): string {
    const filter = this.filterTypes.find(f => f.id === id);
    return filter?.prefix || '';
  }

  getFilterSuffix(id: number): string {
    const filter = this.filterTypes.find(f => f.id === id);
    return filter?.suffix || '';
  }

  getFilterMax(id: number): number | null {
    const filter = this.filterTypes.find(f => f.id === id);
    return filter?.max ?? null;
  }

  // In your AdvancedSearchComponent, update the onSearch method:

  onSearch(): void {
    if (this.form.invalid) {
      const filtersArray = this.filters.controls;
      let hasRangeError = false;
      let hasProximityError = false;

      filtersArray.forEach((filter, index) => {
        const filterTypeValue = filter.get('filterType')?.value;
        const filterType = this.filterTypes.find(f => f.id === filterTypeValue);

        if (filterType?.type === 'range') {
          const valueControl = filter.get('value');
          if (valueControl?.errors?.['incompleteRange']) {
            hasRangeError = true;
            valueControl.markAsTouched();
          }
        }

        if (filterType?.type === 'proximity') {
          const valueControl = filter.get('value');
          if (valueControl?.errors?.['incompleteProximity']) {
            hasProximityError = true;
            valueControl.markAsTouched();
          }
        }
      });

      if (hasRangeError) {
        this.popupService.notify('Please provide both minimum and maximum values for range filters', 'Validation Error', 'error');
        return;
      }

      if (hasProximityError) {
        this.popupService.notify('Please provide an address and a positive radius for proximity filters', 'Validation Error', 'error');
        return;
      }

      // Belt-and-braces: block submit if a range filter's value exceeds its configured max
      // (the in-input clamp should have prevented this, but guard against bypasses).
      let rangeOverMax: { name: string; max: number } | null = null;
      filtersArray.forEach(filter => {
        const filterTypeId = filter.get('filterType')?.value;
        const filterTypeDef = this.filterTypes.find(f => f.id === filterTypeId);
        if (filterTypeDef?.type !== 'range' || filterTypeDef.max == null) return;
        const value = filter.get('value')?.value;
        if (Array.isArray(value)) {
          const exceeds = value.some(v => typeof v === 'number' && v > filterTypeDef.max!);
          if (exceeds) rangeOverMax = { name: filterTypeDef.name, max: filterTypeDef.max };
        }
      });

      if (rangeOverMax) {
        const formatted = (rangeOverMax as { name: string; max: number }).max.toLocaleString('en-US');
        this.popupService.notify(
          `Max value for ${(rangeOverMax as { name: string; max: number }).name} cannot exceed ${formatted}`,
          'Validation Error',
          'error'
        );
        return;
      }

      const locationFiltersWithoutCountry = this.filters.controls.filter(filter => {
        const filterType = this.getFilterType(filter.get('filterType')?.value);
        if (filterType === 'location') {
          const countryControl = this.getLocationCountryControl(filter);
          return !countryControl.value;
        }
        return false;
      });

      if (locationFiltersWithoutCountry.length > 0) {
        locationFiltersWithoutCountry.forEach(filter => {
          this.getLocationCountryControl(filter).markAsTouched();
        });

        this.popupService.notify('Please select a country for all location filters', 'Validation Error', 'error');
        return;
      }

      return;
    }

    const formValue = this.form.getRawValue();
    const companyFilters = [];
    const personFilters = {
      person_titles: null,
      person_seniorities: []
    };

    const enhancedFilters = formValue.filters.map((filter: any) => {
      if (filter.filterType === 10 && filter.value) {
        const enhancedFilter = {...filter, value: {...filter.value}};

        if (filter.value.country) {
          const country = this.countries.find(c => c.id === filter.value.country);
          if (country) {
            enhancedFilter.value.countryData = country;
          }
        }

        if (filter.value.region) {
          const countryRegions = this.regions[filter.value.country] || [];
          const region = countryRegions.find(r => r.id === filter.value.region);
          if (region) {
            enhancedFilter.value.regionData = region;
          }
        }

        if (filter.value.city) {
          const regionCities = this.cities[filter.value.region] || [];
          const city = regionCities.find(c => c.id === filter.value.city);
          if (city) {
            enhancedFilter.value.cityData = city;
          }
        }

        return enhancedFilter;
      }

      return filter;
    });

    // Collect every location filter so they can be merged into a single
    // company_location condition (Veridion uses `in` with an array of
    // locations to express "match any of these", not multiple AND-ed equals).
    const locationValues: { country: string; region?: string; city?: string }[] = [];

    formValue.filters.forEach((filter: any) => {
      if (!filter.filterType) return;

      const filterType = this.filterTypes.find(f => f.id === filter.filterType);
      if (!filterType) return;

      if (this.isFilterValueEmpty(filter, filterType.type)) return;

      if (filterType.type === 'location') {
        const location = this.buildLocationValue(filter.value);
        if (location) {
          locationValues.push(location);
        }
        return;
      }

      if (filterType.applied === 'person') {
        if (filterType.attribute === 'person_titles' && Array.isArray(filter.value)) {
          personFilters.person_titles = {
            match: {
              operator: "or",
              operands: filter.value
            }
          };
        } else if (filterType.attribute === 'person_seniorities' && Array.isArray(filter.value)) {
          personFilters.person_seniorities = filter.value;
        }
      } else {
        const formattedFilter = this.formatFilterForSearch(filter, filterType);
        if (formattedFilter) {
          companyFilters.push(formattedFilter);
        }
      }
    });

    // Merge all location filters into one company_location condition:
    // a single location uses `equals`, multiple locations use `in`.
    if (locationValues.length === 1) {
      companyFilters.push({
        attribute: 'company_location',
        relation: 'equals',
        value: locationValues[0]
      });
    } else if (locationValues.length > 1) {
      companyFilters.push({
        attribute: 'company_location',
        relation: 'in',
        value: locationValues
      });
    }

    const searchParams = {
      filters: {
        and: companyFilters
      },
      page: 1,
      pageSize: 10
    };

    // Save search filters
    this.store.dispatch(AdvancedSearchActions.saveSearchFilters({
      filters: {
        ...formValue,
        filters: enhancedFilters
      },
      filterTypes: this.filterTypes
    }));

    // IMPORTANT: Start search (clears data and sets loading = true) BEFORE navigation
    this.store.dispatch(AdvancedSearchActions.startSearch());

    // Dispatch search action
    this.store.dispatch(AdvancedSearchActions.searchCompanies(searchParams));

    // Check if we need to search for people too
    const companyNameFilter = formValue.filters.find((filter: any) => filter.filterType === 1);
    if (companyNameFilter && companyNameFilter.value) {
      const companyName = companyNameFilter.value;
      const selectedCompany = null;

      this.store.dispatch(AdvancedSearchActions.searchPeople({
        params: {
          companyName: companyName,
          selectedCompany: selectedCompany,
          page: 1,
          perPage: 10,
          ...(personFilters.person_titles && { person_titles: personFilters.person_titles }),
          ...(personFilters.person_seniorities.length > 0 && { person_seniorities: personFilters.person_seniorities })
        }
      }));
    }

    // Navigate to companies page
    this.router.navigate(['/advanced-search/companies']);
  }

  private isFilterValueEmpty(filter: any, filterType: string): boolean {
    if (filterType === 'range') {
      return !Array.isArray(filter.value) ||
        (filter.value[0] === null && filter.value[1] === null) ||
        (filter.value[0] === '' && filter.value[1] === '');
    }

    if (filterType === 'proximity') {
      const v = filter.value;
      if (!v || typeof v !== 'object') return true;
      const addressEmpty = !v.address || (typeof v.address === 'string' && v.address.trim() === '');
      const radiusEmpty = v.radius === null || v.radius === undefined || v.radius === '' || Number(v.radius) <= 0;
      return addressEmpty || radiusEmpty;
    }

    if (filterType === 'tags' || filterType === 'select' || filterType === 'combobox-multiselect') {
      return !Array.isArray(filter.value) || filter.value.length === 0;
    }

    return filter.value === null || filter.value === '';
  }

  private formatFilterForSearch(filter: any, filterType: any): any {
    if (filterType.type === 'range' && Array.isArray(filter.value)) {
      const rangeValues = filter.value.map(v => v === '' ? null : v);

      return {
        attribute: filterType.attribute,
        relation: filterType.relation,
        value: rangeValues
      };
    }

    if (filterType.type === 'proximity' && filter.value && typeof filter.value === 'object') {
      const { address, radius, unit, strictness } = filter.value;
      const formatted: any = {
        attribute: filterType.attribute,
        relation: filterType.relation,
        value: {
          address: typeof address === 'string' ? address.trim() : address,
          radius: Number(radius),
          unit
        }
      };
      // Veridion defaults to strictness=1 (main location only); omit when default
      // to keep the payload minimal.
      if (strictness === 2 || strictness === 3) {
        formatted.strictness = strictness;
      }
      return formatted;
    }

    if (filterType.type === 'tags' && Array.isArray(filter.value)) {
      return {
        attribute: filterType.attribute,
        relation: filterType.relation,
        value: {
          match: {
            operator: "or",
            operands: filter.value
          }
        }
      };
    }

    if (filterType.type === 'select') {
      if (Array.isArray(filter.value) && filter.value.length > 1) {
        return {
          attribute: filterType.attribute,
          relation: "in",
          value: filter.value
        };
      } else {
        return {
          attribute: filterType.attribute,
          relation: "equals",
          value: Array.isArray(filter.value) ? filter.value[0] : filter.value
        };
      }
    }

    if (filterType.type === 'combobox-multiselect' && Array.isArray(filter.value)) {
      if (filter.value.length > 1) {
        return {
          attribute: filterType.attribute,
          relation: "in",
          value: filter.value
        };
      } else if (filter.value.length === 1) {
        return {
          attribute: filterType.attribute,
          relation: "equals",
          value: filter.value[0]
        };
      }
      return null;
    }

    if (filterType.type === 'location' && typeof filter.value === 'object') {
      const locationSearch = this.buildLocationValue(filter.value) || {};

      return {
        attribute: filterType.attribute,
        relation: "equals",
        value: locationSearch
      };
    }

    return {
      attribute: filterType.attribute,
      relation: filterType.relation,
      value: filter.value
    };
  }

  // Maps a location form value (country/region/city ids) to the Veridion
  // payload shape (names). Returns null when no country is resolved, since
  // country is mandatory for every location.
  private buildLocationValue(locationValue: any): { country: string; region?: string; city?: string } | null {
    if (!locationValue || typeof locationValue !== 'object') {
      return null;
    }

    const result: { country?: string; region?: string; city?: string } = {};

    if (locationValue.country) {
      const country = this.countries.find(c => c.id === locationValue.country);
      if (country) {
        result.country = country.name;
      }
    }

    if (!result.country) {
      return null;
    }

    if (locationValue.region) {
      const countryRegions = this.regions[locationValue.country] || [];
      const region = countryRegions.find(r => r.id === locationValue.region);
      if (region) {
        result.region = region.name;
      }
    }

    if (locationValue.city) {
      const regionCities = this.cities[locationValue.region] || [];
      const city = regionCities.find(c => c.id === locationValue.city);
      if (city) {
        result.city = city.name;
      }
    }

    return result as { country: string; region?: string; city?: string };
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.advancedSearchSuccessSubscription) {
      this.advancedSearchSuccessSubscription.unsubscribe();
    }
    if (this.advancedSearchFailureSubscription) {
      this.advancedSearchFailureSubscription.unsubscribe();
    }
    if (this.industriesSubscription) {
      this.industriesSubscription.unsubscribe();
    }
    if (this.countriesSubscription) {
      this.countriesSubscription.unsubscribe();
    }

    Object.values(this.regionsSubscriptions).forEach(sub => sub.unsubscribe());
    Object.values(this.citiesSubscriptions).forEach(sub => sub.unsubscribe());
  }

  resetFilters() {
    const filtersArray = this.form.get('filters') as FormArray;

    while (filtersArray.length > 0) {
      filtersArray.removeAt(0);
    }

    this.addDefaultLocationFilter();
    this.store.dispatch(AdvancedSearchActions.clearSavedFilters());
  }

  getFilterTypeControl(filter: AbstractControl): FormControl {
    return filter.get('filterType') as FormControl;
  }

  onCountrySelected(index: number, country: BaseOption | null): void {
    const filterGroup = this.filters.at(index) as FormGroup;
    const regionControl = this.getLocationRegionControl(filterGroup);
    const cityControl = this.getLocationCityControl(filterGroup);

    if (!country) {
      // Clear everything when country is cleared or modified
      regionControl.setValue(null);
      regionControl.disable();
      cityControl.setValue(null);
      cityControl.disable();

      // Clear the stored regions and cities for all countries
      // This ensures dropdown shows empty when re-enabled
      const countryControl = this.getLocationCountryControl(filterGroup);
      const previousCountryId = countryControl.value;
      if (previousCountryId) {
        delete this.regions[previousCountryId];
        // Clear all cities as well
        Object.keys(this.cities).forEach(key => {
          delete this.cities[key];
        });
      }

      return;
    }

    // If selecting a new country (different from current), clear region and city
    const currentCountryId = this.getLocationCountryControl(filterGroup).value;
    if (currentCountryId && currentCountryId !== country.id) {
      regionControl.setValue(null);
      cityControl.setValue(null);
      cityControl.disable();
    }

    // Enable region, disable city until region is selected
    regionControl.enable();
    cityControl.disable();

    // Check if regions are already loaded for this country
    if (!this.regions[country.id] || this.regions[country.id].length === 0) {
      // Regions not loaded or empty, load them
      this.loadRegionsForCountry(country);
    }
  }

  private loadRegionsForCountry(country: BaseOption): void {
    const countryId = country.id;

    // Check if we're already subscribed to this country's regions
    if (this.regionsSubscriptions[countryId]) {
      return; // Already loading or loaded
    }

    // Check if regions are already loaded for this country
    this.regionsSubscriptions[countryId] = this.store.select(selectRegionsForCountry(countryId))
      .pipe(take(1))
      .subscribe((regions: BaseOption[]) => {
        if (regions.length === 0) {
          const veridionCountryId = country.value || country.id;
          this.store.dispatch(AdvancedSearchActions.loadRegions({
            countryId: countryId,
            countryName: country.name,
            veridionCountryId: veridionCountryId
          }));
        } else {
          this.regions[countryId] = regions;
          this.cdr.detectChanges();
        }
      });

    // Subscribe to regions updates for this country
    this.store.select(selectRegionsForCountry(countryId))
      .pipe(takeUntil(this.destroy$))
      .subscribe((regions: BaseOption[]) => {
        this.regions[countryId] = regions;
        this.cdr.detectChanges();
      });
  }

  getLocationRegionControl(filter: AbstractControl): FormControl {
    return filter.get('value.region') as FormControl;
  }

  getLocationCityControl(filter: AbstractControl): FormControl {
    return filter.get('value.city') as FormControl;
  }

  onRegionSelected(index: number, region: BaseOption | null): void {
    const filterGroup = this.filters.at(index) as FormGroup;
    const cityControl = this.getLocationCityControl(filterGroup);
    const regionControl = this.getLocationRegionControl(filterGroup);

    if (!region) {
      // Clear city when region is cleared or modified
      cityControl.setValue(null);
      cityControl.disable();

      // Clear the stored cities only for the previous region
      const previousRegionId = regionControl.value;
      if (previousRegionId && this.cities[previousRegionId]) {
        delete this.cities[previousRegionId];
      }

      return;
    }

    // Get the previous region ID before updating
    const previousRegionId = regionControl.value;

    // If selecting a new region (different from current), clear city
    if (previousRegionId && previousRegionId !== region.id) {
      cityControl.setValue(null);
      // Clear cities for the previous region
      if (this.cities[previousRegionId]) {
        delete this.cities[previousRegionId];
      }
    }

    // Enable city control
    cityControl.enable();

    const countryId = this.getLocationCountryControl(filterGroup).value;
    const country = this.countries.find(c => c.id === countryId);

    if (country) {
      // Always check if cities need to be loaded for the new region
      if (!this.cities[region.id] || this.cities[region.id].length === 0) {
        this.loadCitiesForRegion(country, region);
      }
    }
  }

  private loadCitiesForRegion(country: BaseOption, region: BaseOption): void {
    const regionId = region.id;

    // Clear any existing subscription for this region
    if (this.citiesSubscriptions[regionId]) {
      this.citiesSubscriptions[regionId].unsubscribe();
      delete this.citiesSubscriptions[regionId];
    }

    // Check if cities are already loaded for this region
    this.citiesSubscriptions[regionId] = this.store.select(selectCitiesForRegion(regionId))
      .pipe(take(1))
      .subscribe((cities: BaseOption[]) => {
        if (cities.length === 0) {
          const veridionCountryId = country.value || country.id;
          const veridionRegionId = region.value || region.id;

          this.store.dispatch(AdvancedSearchActions.loadCities({
            regionId: regionId,
            countryName: country.name,
            regionName: region.name,
            veridionCountryId: veridionCountryId,
            veridionRegionId: veridionRegionId
          }));
        } else {
          this.cities[regionId] = cities;
          this.cdr.detectChanges();
        }
      });

    // Subscribe to cities updates for this region
    this.store.select(selectCitiesForRegion(regionId))
      .pipe(takeUntil(this.destroy$))
      .subscribe((cities: BaseOption[]) => {
        if (cities.length > 0) {
          this.cities[regionId] = cities;
          this.cdr.detectChanges();
        }
      });
  }

  onCitySelected(index: number, city: BaseOption | null): void {
    if (!city) {
      return;
    }
  }

  getAvailableRegions(countryId: number): BaseOption[] {
    return this.regions[countryId] || [];
  }

  getAvailableCities(regionId: number): BaseOption[] {
    return this.cities[regionId] || [];
  }

  getLocationFormGroup(filter: AbstractControl): FormGroup {
    const valueControl = filter.get('value');

    if (!valueControl || typeof valueControl.value !== 'object') {
      filter.get('value')?.setValue({
        country: null,
        region: null,
        city: null
      });
    }

    return filter.get('value') as FormGroup;
  }

  getLocationCountryControl(filter: AbstractControl): FormControl {
    return filter.get('value.country') as FormControl;
  }

  restoreFiltersFromSaved(savedFilters: any[]) {
    const filtersArray = this.form.get('filters') as FormArray;

    while (filtersArray.length > 0) {
      filtersArray.removeAt(0);
    }

    // The first restored location is the mandatory default (locked, non-removable);
    // any additional locations are restored as regular, removable location filters.
    let defaultLocationAssigned = false;

    savedFilters.forEach((savedFilter, index) => {  // Renamed 'filter' to 'savedFilter'
      let filterGroup: FormGroup;

      if (savedFilter.filterType === 10) {
        const isDefaultLocation = !defaultLocationAssigned;
        defaultLocationAssigned = true;

        filterGroup = this.fb.group({
          filterType: [{value: savedFilter.filterType, disabled: isDefaultLocation}, Validators.required],
          value: this.fb.group({
            country: [savedFilter.value?.country || null, Validators.required],
            region: [{value: savedFilter.value?.region || null, disabled: !savedFilter.value?.country}],
            city: [{value: savedFilter.value?.city || null, disabled: !savedFilter.value?.region}]
          }),
          isDefault: [isDefaultLocation]
        });

        if (savedFilter.value?.countryData) {
          // Ensure countries are loaded
          if (this.countries.length === 0) {
            // Wait for countries to be loaded
            this.store.select(selectCountriesList)
              .pipe(
                filter(countries => countries && countries.length > 0),  // This is the RxJS filter
                take(1)
              )
              .subscribe((countries: BaseOption[]) => {
                this.countries = countries;
                // Now restore the location data
                this.restoreLocationData(filterGroup, savedFilter);
              });
          } else {
            // Countries already loaded, restore location data
            this.restoreLocationData(filterGroup, savedFilter);
          }
        }
      } else {
        // Handle other filter types
        if (savedFilter.filterType === 5 || savedFilter.filterType === 6) {
          const filterType = this.filterTypes.find(f => f.id === savedFilter.filterType);
          if (filterType?.type === 'range') {
            filterGroup = this.fb.group({
              filterType: [savedFilter.filterType, Validators.required],
              value: [savedFilter.value || [null, null], [Validators.required, rangeValidator]]
            });
          }
        } else if (savedFilter.filterType === 4) {
          filterGroup = this.fb.group({
            filterType: [savedFilter.filterType, Validators.required],
            value: [savedFilter.value, Validators.required]
          });

          if (Array.isArray(savedFilter.value) && savedFilter.value.length > 0) {
            const industryFilter = this.filterTypes.find(f => f.id === 4);
            if (industryFilter && industryFilter.options) {
              const matchedOptions = savedFilter.value.map(name =>
                industryFilter.options.find(opt => opt.name === name)
              ).filter(Boolean);

              if (matchedOptions.length > 0) {
                const selectedNames = matchedOptions.map(opt => opt.name);
                filterGroup.get('value').setValue(selectedNames);
              }
            }
          }
        } else if (savedFilter.filterType === 13) {
          filterGroup = this.fb.group({
            filterType: [savedFilter.filterType, Validators.required],
            value: [savedFilter.value, Validators.required]
          });

          if (Array.isArray(savedFilter.value) && savedFilter.value.length > 0) {
            const realIndustryFilter = this.filterTypes.find(f => f.id === 13);
            if (realIndustryFilter && realIndustryFilter.options) {
              const matchedOptions = savedFilter.value.map(name =>
                realIndustryFilter.options.find(opt => opt.name === name)
              ).filter(Boolean);

              if (matchedOptions.length > 0) {
                const selectedNames = matchedOptions.map(opt => opt.name);
                filterGroup.get('value').setValue(selectedNames);
              }
            }
          }
        } else if (savedFilter.filterType === 9) {
          // Company Product used to be a single free-text input; it is now a tags list.
          const productTerms = Array.isArray(savedFilter.value)
            ? savedFilter.value
            : (typeof savedFilter.value === 'string' && savedFilter.value.trim() ? [savedFilter.value.trim()] : []);
          filterGroup = this.fb.group({
            filterType: [savedFilter.filterType, Validators.required],
            value: [productTerms, Validators.required]
          });
        } else if (savedFilter.filterType === 14) {
          const restoredValue = (savedFilter.value && typeof savedFilter.value === 'object')
            ? {
                address:    typeof savedFilter.value.address === 'string' ? savedFilter.value.address : '',
                radius:     savedFilter.value.radius ?? null,
                unit:       savedFilter.value.unit === 'mi' ? 'mi' : 'km',
                strictness: [1, 2, 3].includes(savedFilter.value.strictness) ? savedFilter.value.strictness : 1
              }
            : { address: '', radius: null, unit: 'km', strictness: 1 };

          filterGroup = this.fb.group({
            filterType: [savedFilter.filterType, Validators.required],
            value: [restoredValue, [Validators.required, proximityValidator]]
          });
        } else {
          filterGroup = this.fb.group({
            filterType: [savedFilter.filterType, Validators.required],
            value: [savedFilter.value, Validators.required]
          });
        }
      }

      filtersArray.push(filterGroup);
    });

    this.cdr.detectChanges();
  }

  // Updated helper method with renamed parameter
  private restoreLocationData(filterGroup: FormGroup, savedFilter: any): void {  // Renamed parameter
    if (savedFilter.value?.countryData) {
      const country = savedFilter.value.countryData;

      // Load regions for this country
      if (savedFilter.value?.country) {
        this.loadRegionsForCountry(country);

        // If we have region data, wait for regions to load then restore
        if (savedFilter.value?.regionData) {
          this.store.select(selectRegionsForCountry(country.id))
            .pipe(
              filter(regions => regions && regions.length > 0),  // This is the RxJS filter
              take(1)
            )
            .subscribe((regions: BaseOption[]) => {
              this.regions[country.id] = regions;

              const regionControl = filterGroup.get('value.region') as FormControl;
              if (regionControl) {
                regionControl.enable();
              }

              const region = savedFilter.value.regionData;

              // Load cities for this region
              if (savedFilter.value?.region && region) {
                this.loadCitiesForRegion(country, region);

                // If we have city data, wait for cities to load
                if (savedFilter.value?.cityData) {
                  this.store.select(selectCitiesForRegion(region.id))
                    .pipe(
                      filter(cities => cities && cities.length > 0),  // This is the RxJS filter
                      take(1)
                    )
                    .subscribe((cities: BaseOption[]) => {
                      this.cities[region.id] = cities;

                      const cityControl = filterGroup.get('value.city') as FormControl;
                      if (cityControl) {
                        cityControl.enable();
                      }

                      this.cdr.detectChanges();
                    });
                }
              }

              this.cdr.detectChanges();
            });
        }
      }
    }
  }

  getParentLocationForRegion(filter: AbstractControl): { country?: string } {
    const countryControl = this.getLocationCountryControl(filter);

    if (countryControl.value) {
      const country = this.countries.find(c => c.id === countryControl.value);
      return { country: country?.name };
    }

    return {};
  }

  getParentLocationForCity(filter: AbstractControl): { country?: string, region?: string } {
    const countryControl = this.getLocationCountryControl(filter);
    const regionControl = this.getLocationRegionControl(filter);

    if (countryControl.value && regionControl.value) {
      const country = this.countries.find(c => c.id === countryControl.value);
      const region = this.regions[countryControl.value]?.find(r => r.id === regionControl.value);

      return {
        country: country?.name,
        region: region?.name
      };
    }

    return {};
  }

  isLocationFilter(filter: AbstractControl): boolean {
    const filterTypeValue = filter.get('filterType')?.value;
    return filterTypeValue === 10;
  }

  // The mandatory, first location filter that cannot be changed or removed.
  isDefaultLocation(filter: AbstractControl): boolean {
    return filter.get('isDefault')?.value === true;
  }

  get filterTypesExcludingLocation(): any[] {
    return this.filterTypes.filter(type => type.id !== 10 && !type.hidden);
  }

  // Full list (Company Location included) so users can add extra location filters.
  get filterTypesSelectable(): FilterOption[] {
    return this.filterTypes.filter(type => !type.hidden);
  }

  onRegionCleared(index: number): void {
    const filterGroup = this.filters.at(index) as FormGroup;
    const cityControl = this.getLocationCityControl(filterGroup);
    const regionControl = this.getLocationRegionControl(filterGroup);

    // Get the region ID that's being cleared
    const regionId = regionControl.value;

    // Clear and disable city
    cityControl.setValue(null);
    cityControl.disable();

    // Clear only the cities for this specific region
    if (regionId && this.cities[regionId]) {
      delete this.cities[regionId];
    }
  }

  onCityCleared(index: number): void {
    // Nothing special needed - the city is just cleared
    // The typeahead component handles the clearing
  }

  // Add this method to handle when user clicks dropdown for regions
  onRegionDropdownClicked(index: number): void {
    const filterGroup = this.filters.at(index) as FormGroup;
    const countryId = this.getLocationCountryControl(filterGroup).value;

    if (countryId) {
      const country = this.countries.find(c => c.id === countryId);

      // Check if regions are empty and reload if needed
      if (country && (!this.regions[countryId] || this.regions[countryId].length === 0)) {
        this.loadRegionsForCountry(country);
      }
    }
  }

  // Add this method to handle when user clicks dropdown for cities
  onCityDropdownClicked(index: number): void {
    const filterGroup = this.filters.at(index) as FormGroup;
    const countryId = this.getLocationCountryControl(filterGroup).value;
    const regionId = this.getLocationRegionControl(filterGroup).value;

    if (countryId && regionId) {
      const country = this.countries.find(c => c.id === countryId);
      const region = this.regions[countryId]?.find(r => r.id === regionId);

      // Check if cities are empty and reload if needed
      if (country && region && (!this.cities[regionId] || this.cities[regionId].length === 0)) {
        this.loadCitiesForRegion(country, region);
      }
    }
  }

  protected readonly FormGroup = FormGroup;
}
